import { RequestError } from '#errors/index.ts';
import type {
  TRoom,
  TRoomHandle,
  TRoomPayload,
  TRooms,
  TRoomState,
  TRoomTransport,
} from './rooms.types.ts';

const ignoreFailure = () => undefined;

const isRetryable = (error: unknown): boolean =>
  error instanceof RequestError && error.detail.retryable;

const leftBeforeAdmission = (): RequestError =>
  new RequestError({ kind: 'aborted', retryable: false });

const createHandle = (): { handle: TRoomHandle; ready: Promise<void> } => {
  let handle: TRoomHandle = {
    resolve: () => undefined,
    reject: () => undefined,
  };

  const ready = new Promise<void>((resolve, reject) => {
    handle = { resolve, reject };
  });

  ready.catch(ignoreFailure);

  return { handle, ready };
};

const createRoom = (payload: TRoomPayload): TRoomState => ({
  payload,
  isWanted: false,
  isJoined: false,
  isBusy: false,
  isStale: false,
  holders: new Set(),
});

export const createRooms = (transport: TRoomTransport): TRooms => {
  const rooms = new Map<string, TRoomState>();

  const refuse = (room: TRoomState, error: unknown): void => {
    room.isWanted = false;
    room.holders.forEach((holder) => holder.reject(error));
    room.holders.clear();
  };

  const apply = async (room: TRoomState): Promise<void> => {
    if (room.isWanted) {
      await transport.join(room.payload);
      room.isJoined = true;

      return;
    }

    await transport.leave(room.payload);
    room.isJoined = false;
  };

  const settleFailure = (room: TRoomState, error: unknown): void => {
    if (room.isWanted) {
      refuse(room, error);

      return;
    }

    room.isJoined = false;
  };

  const reconcile = async (id: string): Promise<void> => {
    const room = rooms.get(id);

    if (!room) {
      return;
    }

    if (room.isBusy) {
      room.isStale = true;

      return;
    }

    room.isStale = false;

    if (room.isWanted === room.isJoined) {
      if (room.isWanted) {
        room.holders.forEach((holder) => holder.resolve());
      } else {
        rooms.delete(id);
      }

      return;
    }

    let isSettled = true;

    room.isBusy = true;

    try {
      await apply(room);
    } catch (error) {
      isSettled = !isRetryable(error);

      if (isSettled) {
        settleFailure(room, error);
      }
    } finally {
      room.isBusy = false;
    }

    if (isSettled || room.isStale) {
      await reconcile(id);
    }
  };

  const join = (payload: TRoomPayload): TRoom => {
    const id = payload.conversation_id;
    const room = rooms.get(id) ?? createRoom(payload);
    const { handle, ready } = createHandle();

    rooms.set(id, room);
    room.payload = payload;
    room.isWanted = true;
    room.holders.add(handle);
    reconcile(id);

    const leave = async (): Promise<void> => {
      if (!room.holders.delete(handle)) {
        return;
      }

      const reason = leftBeforeAdmission();

      handle.reject(reason);

      if (room.holders.size > 0) {
        return;
      }

      room.isWanted = false;
      await reconcile(id);
    };

    return { ready, leave };
  };

  const rejoinAll = async (): Promise<void> => {
    rooms.forEach((room) => {
      room.isJoined = false;
    });

    await Promise.all([...rooms.keys()].map(reconcile));
  };

  return { join, rejoinAll };
};
